import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { AlertTriangle, Minus, Plus, ShoppingBag, Trash2, X } from 'lucide-react';
import { cn, formatCOP, formatDistance } from '@/lib/cn';
import {
  cantidadTotal,
  subtotalCarrito,
  totalLinea,
  useCarrito,
} from '@/lib/carrito';
import { useCotizacionEnvio } from '@/lib/queries';
import { puntoDeBusqueda, useUbicacion } from '@/lib/ubicacion';
import { Boton, Vacio } from './ui';

export function PanelCarrito({ abierto, onCerrar }: { abierto: boolean; onCerrar: () => void }) {
  const { comercio, lineas, cambiarCantidad, quitar, vaciar } = useCarrito();
  const ubicacion = useUbicacion((s) => s.ubicacion);
  const punto = puntoDeBusqueda(ubicacion);

  useEffect(() => {
    if (!abierto) return;
    const alPresionar = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', alPresionar);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', alPresionar);
      document.body.style.overflow = '';
    };
  }, [abierto, onCerrar]);

  const subtotal = subtotalCarrito(lineas);

  /**
   * El domicilio lo cotiza el servidor: mide con PostGIS la distancia entre el
   * punto de recogida del comercio y la dirección de entrega, y aplica la
   * tarifa de la zona que cubre el destino. Aquí solo se muestra.
   *
   * El hook va antes del early return: los hooks deben ejecutarse siempre en
   * el mismo orden, y salir antes rompería esa regla en el siguiente render.
   */
  const cotizacion = useCotizacionEnvio(comercio?.id, ubicacion ? punto : null, subtotal);

  if (!abierto) return null;

  const minimo = comercio?.minOrderAmount ?? 0;
  const faltaParaMinimo = Math.max(0, minimo - subtotal);

  /**
   * Sin cobertura no se puede pedir.
   *
   * Antes el envío se mostraba en $0 y el total parecía correcto, cuando en
   * realidad el pedido era imposible de entregar.
   */
  const sinCobertura = !!ubicacion && cotizacion.data?.covered === false;
  const cotizando = !!ubicacion && cotizacion.isLoading;
  const envio = cotizacion.data?.fee ?? 0;
  const puedeContinuar =
    !sinCobertura && !cotizando && faltaParaMinimo === 0 && !!ubicacion && !!cotizacion.data;

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onCerrar} aria-hidden />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Tu pedido"
        className="relative flex h-full w-full max-w-md flex-col bg-white shadow-xl animate-slide-up sm:animate-none"
      >
        <header className="flex items-center justify-between border-b border-kick-gray/15 p-4">
          <div>
            <h2 className="text-xl text-kick-dark">Tu pedido</h2>
            {comercio && <p className="text-sm text-kick-gray">{comercio.name}</p>}
          </div>
          <button
            onClick={onCerrar}
            aria-label="Cerrar el carrito"
            className="rounded-kick p-2 text-kick-gray transition hover:bg-kick-light"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {lineas.length === 0 ? (
          <div className="flex flex-1 items-center justify-center p-6">
            <Vacio
              icono={<ShoppingBag className="h-6 w-6" />}
              titulo="Tu carrito está vacío"
              descripcion="Agrega productos de un comercio y aparecerán aquí."
            />
          </div>
        ) : (
          <>
            <ul className="flex-1 divide-y divide-kick-gray/10 overflow-y-auto">
              {lineas.map((linea) => (
                <li key={linea.key} className="flex gap-3 p-4">
                  {linea.imageUrl ? (
                    <img
                      src={linea.imageUrl}
                      alt=""
                      className="h-16 w-16 shrink-0 rounded-kick bg-kick-light object-contain"
                    />
                  ) : (
                    <div className="grid h-16 w-16 shrink-0 place-items-center rounded-kick bg-kick-light text-kick-gray/40">
                      <ShoppingBag className="h-5 w-5" />
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-kick-ink">{linea.name}</p>

                    {linea.opciones.length > 0 && (
                      <ul className="mt-0.5 text-sm text-kick-gray">
                        {linea.opciones.map((o) => (
                          <li key={o.optionId}>
                            {o.optionName}
                            {o.priceDelta > 0 && ` (+${formatCOP(o.priceDelta)})`}
                          </li>
                        ))}
                      </ul>
                    )}

                    {linea.notas && (
                      <p className="mt-0.5 text-sm italic text-kick-gray">«{linea.notas}»</p>
                    )}

                    <div className="mt-2 flex items-center gap-3">
                      <div className="flex items-center gap-1 rounded-kick border border-kick-gray/25">
                        <button
                          onClick={() => cambiarCantidad(linea.key, linea.quantity - 1)}
                          aria-label={`Quitar uno de ${linea.name}`}
                          className="p-1.5 text-kick-gray transition hover:text-kick-ink"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-6 text-center text-sm font-semibold">
                          {linea.quantity}
                        </span>
                        <button
                          onClick={() => cambiarCantidad(linea.key, linea.quantity + 1)}
                          aria-label={`Agregar uno de ${linea.name}`}
                          className="p-1.5 text-kick-gray transition hover:text-kick-ink"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>

                      <span className="ml-auto font-semibold text-kick-ink">
                        {formatCOP(totalLinea(linea))}
                      </span>

                      <button
                        onClick={() => quitar(linea.key)}
                        aria-label={`Quitar ${linea.name} del carrito`}
                        className="rounded-kick p-1.5 text-kick-gray transition hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <footer className="border-t border-kick-gray/15 p-4">
              <dl className="space-y-1.5 text-sm">
                <Fila etiqueta="Subtotal" valor={formatCOP(subtotal)} />

                {sinCobertura ? (
                  <Fila etiqueta="Domicilio" valor="No disponible" />
                ) : (
                  <Fila
                    etiqueta={
                      cotizacion.data
                        ? `Domicilio (${formatDistance(cotizacion.data.distanceMeters)})`
                        : 'Domicilio'
                    }
                    valor={
                      !ubicacion
                        ? 'Elige tu dirección'
                        : cotizando
                          ? 'Calculando…'
                          : cotizacion.data?.free
                            ? 'Gratis'
                            : formatCOP(envio)
                    }
                    resaltado={cotizacion.data?.free}
                  />
                )}

                {/* Empujón cuando el envío gratis está cerca. */}
                {cotizacion.data?.freeDeliveryIn != null &&
                  cotizacion.data.freeDeliveryIn > 0 &&
                  cotizacion.data.freeDeliveryIn <= 2_000_000 && (
                    <p className="rounded-kick bg-emerald-50 px-2.5 py-1.5 text-xs text-emerald-800">
                      Agrega {formatCOP(cotizacion.data.freeDeliveryIn)} más y el domicilio va
                      gratis.
                    </p>
                  )}
              </dl>

              <div className="mt-3 flex items-baseline justify-between border-t border-kick-gray/15 pt-3">
                <span className="font-semibold text-kick-ink">
                  {sinCobertura ? 'Subtotal' : 'Total'}
                </span>
                <span className="text-xl font-bold text-kick-dark">
                  {formatCOP(sinCobertura ? subtotal : subtotal + envio)}
                </span>
              </div>

              {sinCobertura && (
                <p className="mt-3 flex items-start gap-1.5 rounded-kick bg-red-50 px-3 py-2 text-sm text-red-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Tu dirección está fuera de nuestra zona de cobertura. Elige otra dirección
                    para continuar.
                  </span>
                </p>
              )}

              {!ubicacion && (
                <p className="mt-3 flex items-start gap-1.5 rounded-kick bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>Elige dónde quieres recibir tu pedido para ver el costo del domicilio.</span>
                </p>
              )}

              {faltaParaMinimo > 0 && !sinCobertura && (
                <p className="mt-3 rounded-kick bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  Te faltan <strong>{formatCOP(faltaParaMinimo)}</strong> para alcanzar el pedido
                  mínimo de este comercio.
                </p>
              )}

              <Link
                to="/checkout"
                className="mt-3 block"
                /**
                 * Al continuar, el panel se cierra.
                 *
                 * Navegar no lo cierra solo: es un panel, no una ruta, así que
                 * sobrevive al cambio de página y queda tapando el checkout al
                 * que acaba de llevar. Sin cobertura o sin mínimo, en cambio,
                 * no debe navegar ni cerrarse: el motivo está escrito ahí
                 * mismo y hay que poder leerlo.
                 */
                onClick={(e) => {
                  if (!puedeContinuar) {
                    e.preventDefault();
                    return;
                  }
                  onCerrar();
                }}
                aria-disabled={!puedeContinuar}
                tabIndex={puedeContinuar ? undefined : -1}
              >
                <Boton className="w-full" disabled={!puedeContinuar}>
                  Continuar · {cantidadTotal(lineas)} producto
                  {cantidadTotal(lineas) === 1 ? '' : 's'}
                </Boton>
              </Link>

              <button
                onClick={vaciar}
                className="mt-2 w-full py-2 text-sm text-kick-gray transition hover:text-red-600"
              >
                Vaciar carrito
              </button>
            </footer>
          </>
        )}
      </aside>
    </div>,
    document.body,
  );
}

function Fila({
  etiqueta,
  valor,
  resaltado,
}: {
  etiqueta: string;
  valor: string;
  resaltado?: boolean;
}) {
  return (
    <div className="flex justify-between">
      <dt className="text-kick-gray">{etiqueta}</dt>
      <dd className={cn(resaltado ? 'font-semibold text-emerald-600' : 'text-kick-ink')}>
        {valor}
      </dd>
    </div>
  );
}
